import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { toast } from "sonner";
import type { AppDispatch, RootState } from "configureStore";
import {
  fetchBoard,
  moveProjectApi,
  moveColumnApi,
  createCardApi,
  createColumnApi,
  markCardSeenApi,
} from "./project-board.service";
import { updateProject } from "services/project.service";
import {
  celebrateWonDeal,
  formatWonToast,
} from "features/project-board/project-motion";
import { computeOrderBetween } from "./ordering";
import {
  BoardState,
  ProjectCardData,
  ProjectColumnData,
  OrderRef,
  ProjectOutcome,
} from "./types";

type RealtimeCardMovePayload = {
  id: string;
  projectBoardColumn: string;
  order?: number | null;
  rebalanced?: boolean;
  columnOrders?: OrderRef[];
  boardId?: string;
};

type RealtimeColumnMovePayload = {
  id: string;
  order?: number | null;
  rebalanced?: boolean;
  columnOrders?: OrderRef[];
};

type BoardLoadSuccessPayload = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  board: any;
  requestId: number;
  filtersKey: string;
};

type BoardLoadErrorPayload = {
  message: string;
  requestId?: number;
  filtersKey?: string;
};

// Unique-enough id for an optimistic placeholder until the server returns the
// real id. Local-only (swapped out by replaceTempCard/Column), so a monotonic
// counter + timestamp is plenty.
let tempIdSeq = 0;
function makeTempId(prefix: string): string {
  tempIdSeq += 1;
  return `${prefix}-${Date.now()}-${tempIdSeq}`;
}

const initialState: BoardState = {
  boardId: null,
  boardName: "",
  isLoading: true,
  isError: false,
  errMsg: "",
  activeLoadRequestId: null,
  loadedFiltersKey: null,
  filtersValue: {},
  columnsById: {},
  columnOrder: [],
  cardsById: {},
  cardIdsByColumn: {},
};

// --- draft helpers ---------------------------------------------------------
function removeCardId(state: BoardState, cardId: string): void {
  Object.keys(state.cardIdsByColumn).forEach((colId) => {
    const arr = state.cardIdsByColumn[colId];
    const idx = arr.indexOf(cardId);
    if (idx !== -1) arr.splice(idx, 1);
  });
}

function orderOfCard(state: BoardState, id: string): number {
  const card = state.cardsById[id];
  const o = card ? card.order : null;
  return o === null || o === undefined ? Number.MAX_SAFE_INTEGER : o;
}

function insertCardSorted(
  state: BoardState,
  colId: string,
  cardId: string
): void {
  if (!state.cardIdsByColumn[colId]) state.cardIdsByColumn[colId] = [];
  const arr = state.cardIdsByColumn[colId];
  if (arr.indexOf(cardId) !== -1) return; // never insert a duplicate
  const order = orderOfCard(state, cardId);
  let i = 0;
  while (i < arr.length && orderOfCard(state, arr[i]) <= order) i += 1;
  arr.splice(i, 0, cardId);
}

// Guarantee a card id appears exactly once, in its current column. Defends
// against an optimistic-add racing its own (or another's) socket echo.
function dedupeCardId(state: BoardState, cardId: string): void {
  removeCardId(state, cardId);
  const card = state.cardsById[cardId];
  const col = card ? card.projectBoardColumn : null;
  if (col) insertCardSorted(state, col, cardId);
}

function reSortColumn(state: BoardState, colId: string): void {
  const arr = state.cardIdsByColumn[colId];
  if (arr) arr.sort((a, b) => orderOfCard(state, a) - orderOfCard(state, b));
}

function reSortColumnOrder(state: BoardState): void {
  state.columnOrder.sort((a, b) => {
    const oa = state.columnsById[a] ? state.columnsById[a].order ?? 0 : 0;
    const ob = state.columnsById[b] ? state.columnsById[b].order ?? 0 : 0;
    return oa - ob;
  });
}

function hasActiveFilters(filtersValue: BoardState["filtersValue"]): boolean {
  return Object.keys(filtersValue).length > 0;
}

function getFiltersKey(filtersValue: unknown): string {
  if (!filtersValue || typeof filtersValue !== "object") return "{}";
  const filters = filtersValue as Record<string, unknown>;
  const sortedFilters = Object.keys(filters)
    .sort()
    .reduce((acc, key) => {
      acc[key] = filters[key];
      return acc;
    }, {} as Record<string, unknown>);
  return JSON.stringify(sortedFilters);
}

function clearBoardData(state: BoardState): void {
  state.loadedFiltersKey = null;
  state.columnsById = {};
  state.columnOrder = [];
  state.cardsById = {};
  state.cardIdsByColumn = {};
}

let boardLoadRequestSeq = 0;
function nextBoardLoadRequestId(): number {
  boardLoadRequestSeq += 1;
  return boardLoadRequestSeq;
}

const slice = createSlice({
  name: "projectBoard",
  initialState,
  reducers: {
    setLoading(state, action: PayloadAction<boolean>) {
      state.isLoading = action.payload;
    },
    setError(state, action: PayloadAction<BoardLoadErrorPayload>) {
      const { message, requestId, filtersKey } = action.payload;
      const currentFiltersKey = getFiltersKey(state.filtersValue);
      if (requestId !== undefined && state.activeLoadRequestId !== requestId) {
        return;
      }
      if (filtersKey !== undefined && filtersKey !== currentFiltersKey) {
        if (state.loadedFiltersKey !== currentFiltersKey) {
          clearBoardData(state);
          state.isError = true;
          state.errMsg = message || "error";
        }
        state.activeLoadRequestId = null;
        state.isLoading = false;
        return;
      }
      state.activeLoadRequestId = null;
      state.isLoading = false;
      state.isError = true;
      state.errMsg = message || "error";
      if (state.loadedFiltersKey !== currentFiltersKey) {
        clearBoardData(state);
      }
    },
    setDestroy() {
      return initialState;
    },
    setFilterValue(
      state,
      action: PayloadAction<{ key: string; value: unknown }>
    ) {
      const { key, value } = action.payload;
      if (value && (!Array.isArray(value) || value.length)) {
        state.filtersValue[key] = value;
      } else {
        delete state.filtersValue[key];
      }
    },
    setBoardName(state, action: PayloadAction<string>) {
      state.boardName = action.payload || "";
    },
    // Begin loading a (possibly different) board. Switching boards drops the
    // previous board's data immediately so stale columns/cards can't be used by
    // DnD / quick-add or merged into by realtime handlers. A same-board refetch
    // (e.g. a filter change) keeps the current data visible while loading.
    startBoardLoad(
      state,
      action: PayloadAction<{ boardId: string; requestId: number }>
    ) {
      const { boardId, requestId } = action.payload;
      const targetId = String(boardId);
      state.activeLoadRequestId = requestId;
      if (state.boardId !== targetId) {
        state.boardId = targetId;
        state.boardName = "";
        clearBoardData(state);
      }
      state.isLoading = true;
      state.isError = false;
    },
    setBoard(state, action: PayloadAction<BoardLoadSuccessPayload>) {
      const { board, requestId, filtersKey } = action.payload;
      const currentFiltersKey = getFiltersKey(state.filtersValue);
      if (state.activeLoadRequestId !== requestId) {
        return;
      }
      if (filtersKey !== currentFiltersKey) {
        if (state.loadedFiltersKey !== currentFiltersKey) {
          clearBoardData(state);
          state.isError = true;
          state.errMsg = "not_loaded";
        }
        state.activeLoadRequestId = null;
        state.isLoading = false;
        return;
      }
      const boardId = String(board.id);
      // Discard a stale response: only apply if it still matches the board we
      // are loading (guards against a slow fetch from a board we've since
      // navigated away from overwriting the current one).
      if (state.boardId && boardId !== state.boardId) {
        return;
      }
      state.boardId = boardId;
      state.boardName = board.name;
      clearBoardData(state);
      const columns = [...(board.columns || [])].sort(
        (a, b) => (a.order ?? 0) - (b.order ?? 0)
      );
      columns.forEach((col) => {
        state.columnsById[col.id] = {
          id: col.id,
          name: col.name,
          order: col.order ?? null,
          projectBoard: boardId,
          rottingDays:
            col.rottingDays === null || col.rottingDays === undefined
              ? null
              : Number(col.rottingDays),
        };
        state.columnOrder.push(col.id);
        state.cardIdsByColumn[col.id] = [];
        const cards = [...(col.projects || [])].sort(
          (a, b) => (a.order ?? 0) - (b.order ?? 0)
        );
        cards.forEach((card: ProjectCardData) => {
          state.cardsById[card.id] = card;
          state.cardIdsByColumn[col.id].push(card.id);
        });
      });
      state.activeLoadRequestId = null;
      state.loadedFiltersKey = filtersKey;
      state.isLoading = false;
      state.isError = false;
    },
    // Roll the layout back to a pre-move snapshot when a move API call fails, so
    // the board never stays out of sync with the server even if the follow-up
    // reload also fails. Only the maps provided are restored.
    restoreSnapshot(
      state,
      action: PayloadAction<{
        columnsById?: BoardState["columnsById"];
        columnOrder?: BoardState["columnOrder"];
        cardsById?: BoardState["cardsById"];
        cardIdsByColumn?: BoardState["cardIdsByColumn"];
      }>
    ) {
      const snap = action.payload;
      if (snap.columnsById) state.columnsById = snap.columnsById;
      if (snap.columnOrder) state.columnOrder = snap.columnOrder;
      if (snap.cardsById) state.cardsById = snap.cardsById;
      if (snap.cardIdsByColumn) state.cardIdsByColumn = snap.cardIdsByColumn;
    },

    // --- card ops ---
    moveCardLocal(
      state,
      action: PayloadAction<{
        cardId: string;
        toColumn: string;
        toIndex: number;
      }>
    ) {
      const { cardId, toColumn, toIndex } = action.payload;
      const card = state.cardsById[cardId];
      if (!card) return;
      removeCardId(state, cardId);
      card.projectBoardColumn = toColumn;
      if (!state.cardIdsByColumn[toColumn])
        state.cardIdsByColumn[toColumn] = [];
      state.cardIdsByColumn[toColumn].splice(toIndex, 0, cardId);
    },
    setCardOrder(
      state,
      action: PayloadAction<{ id: string; order: number | null }>
    ) {
      const { id, order } = action.payload;
      if (state.cardsById[id]) state.cardsById[id].order = order;
    },
    applyColumnOrders(
      state,
      action: PayloadAction<{ columnId: string; columnOrders: OrderRef[] }>
    ) {
      const { columnId, columnOrders } = action.payload;
      (columnOrders || []).forEach(({ id, order }) => {
        if (state.cardsById[id]) state.cardsById[id].order = order;
      });
      if (columnId) reSortColumn(state, columnId);
    },
    applyCardMoved(
      state,
      action: PayloadAction<{
        id: string;
        projectBoardColumn: string;
        order?: number | null;
        rebalanced?: boolean;
        columnOrders?: OrderRef[];
      }>
    ) {
      const { id, projectBoardColumn, order, rebalanced, columnOrders } =
        action.payload;
      const card = state.cardsById[id];
      if (!card) return; // not loaded here — ignore
      removeCardId(state, id);
      // Moved to a column that isn't on this board (a cross-board move) — drop
      // the card from this view instead of orphaning it in a phantom column.
      if (!state.cardIdsByColumn[projectBoardColumn]) {
        delete state.cardsById[id];
        return;
      }
      card.projectBoardColumn = projectBoardColumn;
      if (order !== undefined && order !== null) card.order = order;
      insertCardSorted(state, projectBoardColumn, id);
      if (rebalanced && columnOrders) {
        columnOrders.forEach(({ id: cid, order: o }) => {
          if (state.cardsById[cid]) state.cardsById[cid].order = o;
        });
        reSortColumn(state, projectBoardColumn);
      }
    },
    applyCardUpserted(state, action: PayloadAction<{ card: ProjectCardData }>) {
      const { card } = action.payload;
      if (!card) return;
      const col = card.projectBoardColumn;
      // New column isn't on this board (a cross-board move landed elsewhere) —
      // drop the card from this view instead of orphaning it in a phantom column.
      if (!state.cardIdsByColumn[col]) {
        removeCardId(state, card.id);
        delete state.cardsById[card.id];
        return;
      }
      // `seen` is personal and client-owned: a realtime payload's `seen` was
      // computed for the actor (or defaulted), so never trust it here. Keep my
      // local seen for a card I already have; a brand-new card is unseen until I
      // open it (the creator's own card gets seen via its optimistic path).
      const existing = state.cardsById[card.id];
      const mySeen = existing ? !!existing.seen : false;
      state.cardsById[card.id] = card;
      state.cardsById[card.id].seen = mySeen;
      // Make the upsert authoritative about placement: remove the id from
      // wherever it currently sits and re-insert it once into its column. This
      // handles an inline column change whose event carries no previous-column
      // id (e.g. patch-projects), so the card can't linger as a duplicate.
      dedupeCardId(state, card.id);
    },
    setCardSeen(state, action: PayloadAction<{ id: string; seen: boolean }>) {
      const { id, seen } = action.payload;
      if (state.cardsById[id]) state.cardsById[id].seen = seen;
    },
    applyCardRemoved(state, action: PayloadAction<{ id: string }>) {
      const { id } = action.payload;
      removeCardId(state, id);
      delete state.cardsById[id];
    },
    addTempCard(state, action: PayloadAction<{ card: ProjectCardData }>) {
      const { card } = action.payload;
      state.cardsById[card.id] = card;
      const col = card.projectBoardColumn;
      if (!state.cardIdsByColumn[col]) state.cardIdsByColumn[col] = [];
      state.cardIdsByColumn[col].push(card.id);
    },
    replaceTempCard(
      state,
      action: PayloadAction<{ tempId: string; card: ProjectCardData }>
    ) {
      const { tempId, card } = action.payload;
      // Drop the temp placeholder, set the real card, then ensure it appears
      // exactly once (a socket echo of our own create may have already inserted
      // the real id while the temp was still present).
      removeCardId(state, tempId);
      delete state.cardsById[tempId];
      state.cardsById[card.id] = card;
      dedupeCardId(state, card.id);
    },

    // --- column ops ---
    moveColumnLocal(
      state,
      action: PayloadAction<{ fromIndex: number; toIndex: number }>
    ) {
      const { fromIndex, toIndex } = action.payload;
      const [id] = state.columnOrder.splice(fromIndex, 1);
      state.columnOrder.splice(toIndex, 0, id);
    },
    setColumnOrder(
      state,
      action: PayloadAction<{ id: string; order: number | null }>
    ) {
      const { id, order } = action.payload;
      if (state.columnsById[id]) state.columnsById[id].order = order;
    },
    applyColumnMoved(
      state,
      action: PayloadAction<{
        id: string;
        order?: number | null;
        rebalanced?: boolean;
        columnOrders?: OrderRef[];
      }>
    ) {
      const { id, order, rebalanced, columnOrders } = action.payload;
      if (state.columnsById[id] && order !== null && order !== undefined) {
        state.columnsById[id].order = order;
      }
      if (rebalanced && columnOrders) {
        columnOrders.forEach(({ id: cid, order: o }) => {
          if (state.columnsById[cid]) state.columnsById[cid].order = o;
        });
      }
      reSortColumnOrder(state);
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    applyColumnUpserted(state, action: PayloadAction<{ column: any }>) {
      const { column } = action.payload;
      if (!column) return;
      const id = column.id;
      const existing = state.columnsById[id];
      state.columnsById[id] = {
        id,
        name: column.name,
        order: column.order ?? (existing ? existing.order : null),
        projectBoard: column.projectBoard || state.boardId,
        rottingDays:
          column.rottingDays === null || column.rottingDays === undefined
            ? existing?.rottingDays ?? null
            : Number(column.rottingDays),
      };
      if (!state.columnOrder.includes(id)) {
        state.columnOrder.push(id);
        if (!state.cardIdsByColumn[id]) state.cardIdsByColumn[id] = [];
        reSortColumnOrder(state);
      }
    },
    addTempColumn(state, action: PayloadAction<{ column: ProjectColumnData }>) {
      const { column } = action.payload;
      state.columnsById[column.id] = column;
      state.columnOrder.push(column.id);
      state.cardIdsByColumn[column.id] = [];
    },
    replaceTempColumn(
      state,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      action: PayloadAction<{ tempId: string; column: any }>
    ) {
      const { tempId, column } = action.payload;
      const idx = state.columnOrder.indexOf(tempId);
      if (idx !== -1) state.columnOrder[idx] = column.id;
      state.cardIdsByColumn[column.id] =
        state.cardIdsByColumn[tempId] || state.cardIdsByColumn[column.id] || [];
      delete state.cardIdsByColumn[tempId];
      delete state.columnsById[tempId];
      state.columnsById[column.id] = {
        id: column.id,
        name: column.name,
        order: column.order ?? null,
        projectBoard: column.projectBoard || state.boardId,
      };
      // A socket echo of our own create may have already pushed the real id —
      // keep columnOrder unique.
      state.columnOrder = state.columnOrder.filter(
        (cid, i) => state.columnOrder.indexOf(cid) === i
      );
      reSortColumnOrder(state);
    },
    removeColumn(state, action: PayloadAction<{ id: string }>) {
      const { id } = action.payload;
      const idx = state.columnOrder.indexOf(id);
      if (idx !== -1) state.columnOrder.splice(idx, 1);
      delete state.columnsById[id];
      delete state.cardIdsByColumn[id];
    },
  },
});

export const {
  setLoading,
  setError,
  setDestroy,
  setFilterValue,
  setBoardName,
  startBoardLoad,
  setBoard,
  restoreSnapshot,
  moveCardLocal,
  setCardOrder,
  setCardSeen,
  applyColumnOrders,
  applyCardMoved,
  applyCardUpserted,
  applyCardRemoved,
  addTempCard,
  replaceTempCard,
  moveColumnLocal,
  setColumnOrder,
  applyColumnMoved,
  applyColumnUpserted,
  addTempColumn,
  replaceTempColumn,
  removeColumn,
} = slice.actions;

export default slice.reducer;

// --- thunks ----------------------------------------------------------------

/** Load (or reload) the board. Kept under the original name — other modules
 *  dispatch this to refresh after a create/edit. */
export const fetchProjectBoard =
  (companyId: string, boardId: string, filtersValue?: unknown) =>
    async (dispatch: AppDispatch, getState: () => RootState) => {
      // Default to the board's current filters. A bare call (no filtersValue)
      // would otherwise load unfiltered and tag the response with key "{}", which
      // setBoard rejects whenever a filter is active — leaving isLoading stuck.
      const effectiveFilters =
        filtersValue === undefined
          ? getState().projectBoardReducer.filtersValue
          : filtersValue;
      const requestId = nextBoardLoadRequestId();
      const filtersKey = getFiltersKey(effectiveFilters);
      dispatch(startBoardLoad({ boardId, requestId }));
      try {
        const board = await fetchBoard(companyId, boardId, effectiveFilters);
        dispatch(setBoard({ board, requestId, filtersKey }));
      } catch (e) {
        dispatch(setError({ message: "not_loaded", requestId, filtersKey }));
      }
    };

export const fetchProjectBoardWithCurrentFilters =
  (companyId: string, boardId: string) =>
    async (dispatch: AppDispatch, getState: () => RootState) => {
      const state = getState().projectBoardReducer;
      dispatch(fetchProjectBoard(companyId, boardId, state.filtersValue));
    };

export const applyRealtimeCardUpserted =
  (args: { companyId: string; boardId: string; card: ProjectCardData }) =>
    async (dispatch: AppDispatch, getState: () => RootState) => {
      const { companyId, boardId, card } = args;
      const state = getState().projectBoardReducer;
      if (hasActiveFilters(state.filtersValue)) {
        dispatch(fetchProjectBoard(companyId, boardId, state.filtersValue));
        return;
      }
      dispatch(applyCardUpserted({ card }));
    };

export const applyRealtimeCardMoved =
  (args: {
    companyId: string;
    boardId: string;
    payload: RealtimeCardMovePayload;
  }) =>
    async (dispatch: AppDispatch, getState: () => RootState) => {
      const { companyId, boardId, payload } = args;
      const state = getState().projectBoardReducer;
      const isInboundMove = String(payload.boardId) === String(boardId);
      if (
        hasActiveFilters(state.filtersValue) ||
        (!state.cardsById[payload.id] && isInboundMove)
      ) {
        dispatch(fetchProjectBoard(companyId, boardId, state.filtersValue));
        return;
      }
      dispatch(applyCardMoved(payload));
    };

export const applyRealtimeColumnUpserted =
  (args: { companyId: string; boardId: string; column: unknown }) =>
    async (dispatch: AppDispatch, getState: () => RootState) => {
      const { companyId, boardId, column } = args;
      const state = getState().projectBoardReducer;
      if (hasActiveFilters(state.filtersValue)) {
        dispatch(fetchProjectBoard(companyId, boardId, state.filtersValue));
        return;
      }
      dispatch(applyColumnUpserted({ column }));
    };

export const applyRealtimeColumnMoved =
  (args: {
    companyId: string;
    boardId: string;
    payload: RealtimeColumnMovePayload;
  }) =>
    async (dispatch: AppDispatch, getState: () => RootState) => {
      const { companyId, boardId, payload } = args;
      const state = getState().projectBoardReducer;
      if (hasActiveFilters(state.filtersValue)) {
        dispatch(fetchProjectBoard(companyId, boardId, state.filtersValue));
        return;
      }
      dispatch(applyColumnMoved(payload));
    };

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function normalizeCreatedCard(
  created: any,
  columnId: string,
  fallbackOrder: number
): ProjectCardData {
  return {
    id: created.id,
    name: created.name,
    description: created.description || "",
    projectBoardColumn: created.projectBoardColumn || columnId,
    order: created.order ?? fallbackOrder,
    isImportant: !!created.isImportant,
    isResolved: !!created.isResolved,
    outcome: created.outcome || null,
    outcomeAt: created.outcomeAt || null,
    valueEur:
      created.valueEur === null || created.valueEur === undefined
        ? null
        : Number(created.valueEur),
    contactName: created.contactName || null,
    contactEmail: created.contactEmail || null,
    contactPhone: created.contactPhone || null,
    contactLinkedin: created.contactLinkedin || null,
    contactWhatsapp: created.contactWhatsapp || null,
    contactViber: created.contactViber || null,
    dueDate: created.dueDate || null,
    user: null,
    customer: null,
    reporter: null,
    commentsCount: 0,
    filesCount: 0,
    productsCount: 0,
    totalPrice: 0,
    // The creator has implicitly seen their own new card → show the eye.
    seen: true,
    createdAt: created.createdAt,
    updatedAt: created.updatedAt,
  };
}

export const moveProjectThunk =
  (args: {
    companyId: string;
    cardId: string;
    toColumn: string;
    toIndex: number;
  }) =>
    async (dispatch: AppDispatch, getState: () => RootState) => {
      const { companyId, cardId, toColumn, toIndex } = args;
      // Pre-move snapshot (immutable) to roll back to if the API call fails.
      const snapshot = getState().projectBoardReducer;
      dispatch(moveCardLocal({ cardId, toColumn, toIndex }));
      const state = getState().projectBoardReducer;
      const arr = state.cardIdsByColumn[toColumn] || [];
      const pos = arr.indexOf(cardId);
      const prevId = pos > 0 ? arr[pos - 1] : null;
      const nextId = pos < arr.length - 1 ? arr[pos + 1] : null;
      const order = computeOrderBetween(
        prevId ? state.cardsById[prevId]?.order : null,
        nextId ? state.cardsById[nextId]?.order : null
      );
      dispatch(setCardOrder({ id: cardId, order }));
      try {
        const res = await moveProjectApi(companyId, cardId, {
          projectBoardColumn: toColumn,
          order,
        });
        if (res?.rebalanced && res.columnOrders) {
          dispatch(
            applyColumnOrders({
              columnId: toColumn,
              columnOrders: res.columnOrders,
            })
          );
        } else if (res?.order !== null && res?.order !== undefined) {
          dispatch(setCardOrder({ id: cardId, order: res.order }));
        }
      } catch (e) {
        // Roll back to the pre-move layout first (deterministic), then try to
        // reconcile with the server — so a failed reload can't leave the board in
        // the wrong order.
        dispatch(
          restoreSnapshot({
            cardsById: snapshot.cardsById,
            cardIdsByColumn: snapshot.cardIdsByColumn,
          })
        );
        toast.error("Could not move card");
        dispatch(
          fetchProjectBoard(
            companyId,
            snapshot.boardId as string,
            snapshot.filtersValue
          )
        );
      }
    };

export const moveColumnThunk =
  (args: {
    companyId: string;
    columnId: string;
    fromIndex: number;
    toIndex: number;
  }) =>
    async (dispatch: AppDispatch, getState: () => RootState) => {
      const { companyId, columnId, fromIndex, toIndex } = args;
      // Pre-move snapshot (immutable) to roll back to if the API call fails.
      const snapshot = getState().projectBoardReducer;
      dispatch(moveColumnLocal({ fromIndex, toIndex }));
      const state = getState().projectBoardReducer;
      const arr = state.columnOrder;
      const pos = arr.indexOf(columnId);
      const prevId = pos > 0 ? arr[pos - 1] : null;
      const nextId = pos < arr.length - 1 ? arr[pos + 1] : null;
      const order = computeOrderBetween(
        prevId ? state.columnsById[prevId]?.order : null,
        nextId ? state.columnsById[nextId]?.order : null
      );
      dispatch(setColumnOrder({ id: columnId, order }));
      try {
        const res = await moveColumnApi(companyId, columnId, { order });
        if (res?.rebalanced && res.columnOrders) {
          dispatch(
            applyColumnMoved({
              id: columnId,
              order: res.order,
              rebalanced: true,
              columnOrders: res.columnOrders,
            })
          );
        } else if (res?.order !== null && res?.order !== undefined) {
          dispatch(setColumnOrder({ id: columnId, order: res.order }));
        }
      } catch (e) {
        // Roll back to the pre-move layout first (deterministic), then try to
        // reconcile with the server — so a failed reload can't leave the lists in
        // the wrong order.
        dispatch(
          restoreSnapshot({
            columnsById: snapshot.columnsById,
            columnOrder: snapshot.columnOrder,
          })
        );
        toast.error("Could not move list");
        dispatch(
          fetchProjectBoard(
            companyId,
            snapshot.boardId as string,
            snapshot.filtersValue
          )
        );
      }
    };

export const addCardThunk =
  (args: {
    companyId: string;
    boardId: string;
    columnId: string;
    name: string;
  }) =>
    async (dispatch: AppDispatch, getState: () => RootState) => {
      const { companyId, boardId, columnId, name } = args;
      const state = getState().projectBoardReducer;
      const arr = state.cardIdsByColumn[columnId] || [];
      const lastId = arr[arr.length - 1];
      const order = computeOrderBetween(
        lastId ? state.cardsById[lastId]?.order : null,
        null
      );
      const tempId = makeTempId("temp");
      dispatch(
        addTempCard({
          card: {
            id: tempId,
            name,
            description: "",
            projectBoardColumn: columnId,
            order,
            isImportant: false,
            isResolved: false,
            outcome: null,
            outcomeAt: null,
            valueEur: null,
            contactName: null,
            contactEmail: null,
            contactPhone: null,
            contactLinkedin: null,
            contactWhatsapp: null,
            contactViber: null,
            dueDate: null,
            user: null,
            customer: null,
            reporter: null,
            commentsCount: 0,
            filesCount: 0,
            productsCount: 0,
            totalPrice: 0,
            seen: true,
            __optimistic: true,
          },
        })
      );
      try {
        const created = await createCardApi(companyId, {
          name,
          projectBoardColumn: columnId,
          projectBoardId: boardId,
          order,
          products: [],
        });
        // The board may have been switched while the create was in flight;
        // startBoardLoad would have cleared the temp placeholder. Reconciling now
        // would write a phantom card (and column bucket) into the newly loaded
        // board, so only apply if we're still on the originating board.
        if (String(getState().projectBoardReducer.boardId) !== String(boardId)) {
          return;
        }
        dispatch(
          replaceTempCard({
            tempId,
            card: normalizeCreatedCard(created, columnId, order),
          })
        );
      } catch (e) {
        if (String(getState().projectBoardReducer.boardId) !== String(boardId)) {
          return;
        }
        toast.error("Could not add card");
        dispatch(applyCardRemoved({ id: tempId }));
      }
    };

export const addColumnThunk =
  (args: { companyId: string; boardId: string; name: string }) =>
    async (dispatch: AppDispatch, getState: () => RootState) => {
      const { companyId, boardId, name } = args;
      const state = getState().projectBoardReducer;
      const arr = state.columnOrder;
      const lastId = arr[arr.length - 1];
      const order = computeOrderBetween(
        lastId ? state.columnsById[lastId]?.order : null,
        null
      );
      const tempId = makeTempId("temp-col");
      dispatch(
        addTempColumn({
          column: {
            id: tempId,
            name,
            order,
            projectBoard: boardId,
            __optimistic: true,
          },
        })
      );
      try {
        const created = await createColumnApi(companyId, {
          name,
          projectBoardId: boardId,
          order,
        });
        // Guard against a board switch while the create was in flight — see
        // addCardThunk: a late reconcile would inject a phantom list/bucket.
        if (String(getState().projectBoardReducer.boardId) !== String(boardId)) {
          return;
        }
        dispatch(replaceTempColumn({ tempId, column: created }));
      } catch (e) {
        if (String(getState().projectBoardReducer.boardId) !== String(boardId)) {
          return;
        }
        toast.error("Could not add list");
        dispatch(removeColumn({ id: tempId }));
      }
    };

/** Mark a card seen by the current user (sticky). Optimistic: flip the eye on
 *  immediately, then persist. The server broadcasts to the user's other tabs. */
export const markCardSeen =
  (companyId: string, cardId: string) =>
    async (dispatch: AppDispatch) => {
      dispatch(setCardSeen({ id: cardId, seen: true }));
      try {
        await markCardSeenApi(companyId, cardId);
      } catch (e) {
        // Best-effort + sticky: keep the optimistic seen; the next board load
        // reconciles from the server.
      }
    };

/** Set CRM outcome from a board drop zone (WON / LOST / DELETED). */
export const setCardOutcomeThunk =
  (args: {
    companyId: string;
    cardId: string;
    outcome: ProjectOutcome;
  }) =>
    async (dispatch: AppDispatch, getState: () => RootState) => {
      const { companyId, cardId, outcome } = args;
      const card = getState().projectBoardReducer.cardsById[cardId];
      if (!card) return;
      const previous = { ...card };
      dispatch(
        applyCardUpserted({
          card: {
            ...card,
            outcome,
            outcomeAt: Date.now(),
            lastActivityAt: Date.now(),
          },
        })
      );
      try {
        await updateProject(
          {
            id: cardId,
            name: card.name,
            description: card.description || "",
            projectBoardColumn: card.projectBoardColumn,
            user: card.user?.id,
            isImportant: card.isImportant,
            isResolved: card.isResolved,
            dueDate: card.dueDate,
            order: card.order,
            outcome,
            valueEur: card.valueEur ?? null,
            contactName: card.contactName ?? null,
            contactEmail: card.contactEmail ?? null,
            contactPhone: card.contactPhone ?? null,
            contactLinkedin: card.contactLinkedin ?? null,
            contactWhatsapp: card.contactWhatsapp ?? null,
            contactViber: card.contactViber ?? null,
          },
          companyId
        );
        if (outcome === "WON") {
          await celebrateWonDeal();
          toast.success(formatWonToast(card.valueEur));
        } else {
          toast.success(
            outcome === "LOST" ? "Marked as Lost" : "Marked as Deleted"
          );
        }
      } catch (e) {
        dispatch(applyCardUpserted({ card: previous }));
        toast.error("Could not update outcome");
      }
    };

/** Move a card to another board's column (MOVE TO drop zone). */
export const moveProjectToBoardThunk =
  (args: {
    companyId: string;
    cardId: string;
    toColumnId: string;
    toBoardId: string;
  }) =>
    async (dispatch: AppDispatch, getState: () => RootState) => {
      const { companyId, cardId, toColumnId } = args;
      const snapshot = getState().projectBoardReducer;
      if (!snapshot.cardsById[cardId]) return;
      dispatch(applyCardRemoved({ id: cardId }));
      try {
        await moveProjectApi(companyId, cardId, {
          projectBoardColumn: toColumnId,
          order: null,
        });
        toast.success("Moved to another board");
      } catch (e) {
        toast.error("Could not move card");
        dispatch(
          fetchProjectBoard(
            companyId,
            snapshot.boardId as string,
            snapshot.filtersValue
          )
        );
        throw e;
      }
    };
